import React, { useRef } from 'react'
import dayjs from 'dayjs'
import questionService from '@/api/question'
import { i18n } from '@/locale/i18n'
import useScoreChangeBurst from '@/components/ui/confetti/useScoreChangeBurst'
import {
  shouldShowSportsScore,
  showPenaltiesScore,
  hasExtraTimeScore,
  getMainSportsScores,
  getSportsLeague,
  isSportsMarketResolved,
  isSportsMatchLive,
} from '@/features/market/utils/sportsEvent'
import { getTeamSideImage } from '@/features/market/utils/teamFlag'
import Thumbnail from '@/components/ui/thumbnail'

const LiveIndicator = () => (
  <span className="relative flex h-3 w-3 shrink-0 items-center justify-center" aria-hidden>
    <span className="absolute inline-flex h-full w-full animate-ping rounded-full border border-negative-4 opacity-60 [animation-duration:1.8s]" />
    <span className="absolute inline-flex h-full w-full animate-ping rounded-full border border-negative-4 opacity-40 [animation-duration:1.8s] [animation-delay:0.9s]" />
    <span className="relative inline-flex h-1.5 w-1.5 animate-pulse rounded-full bg-negative-4 [animation-duration:2s]" />
  </span>
)

const SportEventQuestionHeader = ({ question }) => {
  const sportsData = question?.event_extra_data || {}
  const league = getSportsLeague(sportsData)
  const eventStartDate = question?.event_start_date
  const scoreRef = useRef(null)
  const showingScore = shouldShowSportsScore(question, sportsData)
  const showingPenalties = showPenaltiesScore(sportsData)
  const showingExtraTime = hasExtraTimeScore(sportsData)
  const mainScores = getMainSportsScores(sportsData)

  useScoreChangeBurst(scoreRef, {
    scores: {
      homeRegular: sportsData.home_score_regular_time,
      awayRegular: sportsData.away_score_regular_time,
      homeExtraTime: sportsData.home_score_extra_time,
      awayExtraTime: sportsData.away_score_extra_time,
      homePenalties: sportsData.home_score_penalties,
      awayPenalties: sportsData.away_score_penalties,
    },
    enabled: showingScore,
  })

  const isResolved = isSportsMarketResolved(question)
  const isMatchLive = isSportsMatchLive(question)
  const getMonthShort = () => dayjs(eventStartDate).format('DD MMM')
  const getResolvedEventDate = () => dayjs(eventStartDate).format('DD MMM, YYYY')
  const getTime = () => dayjs(eventStartDate).format('hh:mm')
  const getAmPm = () => dayjs(eventStartDate).format('A')
  const isToday = () => dayjs(eventStartDate).isSame(dayjs(), 'day')
  const sportTeamLogoCard = (side, shortCode, teamName) => {
    const image = getTeamSideImage(question, side)
    const label = teamName || shortCode
    if (!image && !label) return null

    return (
      <div className="flex min-w-0 flex-1 basis-0 flex-col gap-1">
        <div className="flex justify-center items-center">
          {image && <Thumbnail image={image} alt={label || ''} className="w-10 h-10 rounded-full" variant="rect" eager />}
        </div>
        <div className="line-clamp-2 break-words text-center text-sm font-bold leading-[120%]">{label}</div>
      </div>
    )
  }

  const headerCenterTopContent = () => (
    <div>
      {showingScore && (
        <div ref={scoreRef} className="relative flex flex-col items-center text-center text-Neutral-4">
          <div className="min-h-[27px] text-xl font-bold leading-[133%]">
            {mainScores.home} - {mainScores.away}
          </div>
        </div>
      )}
      {!showingScore && (
        <div className="h-[27px] text-xl text-Neutral-4 font-bold leading-[133%] text-center">
          {isToday() ? i18n('common.today') : getMonthShort()}
        </div>
      )}
    </div>
  )

  const getLiveStatusLabel = () => {
    if (showingPenalties) return i18n('question_card.sport_event.penalties')
    if (showingExtraTime) return i18n('question_card.sport_event.extra_time')
    return i18n('question_card.sport_event.live')
  }

  const renderLiveStatus = () => (
    <div className="flex flex-col items-center justify-center">
      <div
        className="flex flex-row gap-1.5 items-center justify-center text-negative-4 font-medium uppercase"
        aria-label={getLiveStatusLabel()}
      >
        <LiveIndicator />
        <span>{getLiveStatusLabel()}</span>
      </div>

      {showingPenalties && (
        <div className="text-base font-normal leading-[160%] text-Neutral-4">
          {sportsData.home_score_penalties} - {sportsData.away_score_penalties}
        </div>
      )}

      {!showingPenalties && showingExtraTime && (
        <div className="text-base font-normal leading-[160%] text-Neutral-4">
          {sportsData.home_score_extra_time} - {sportsData.away_score_extra_time}
        </div>
      )}
    </div>
  )

  const headerCenterBottomContent = () => {
    const isPaused = questionService.isPaused(question)
    const isPending = questionService.isWaitingForResults(question)

    return (
      <div>
        <div className="min-h-[22px] text-Neutral-4 text-sm font-normal leading-[160%] text-center">
          <div>
            {isResolved && eventStartDate && <div>{getResolvedEventDate()}</div>}

            {!isResolved && !showingScore && !isPaused && !isPending && !isMatchLive && (
              <div>
                {getTime()} {getAmPm()}
              </div>
            )}

            {!isResolved && !isMatchLive && isPaused && <div>{i18n('bet.reviewing')}</div>}

            {!isResolved && !isMatchLive && !isPaused && isPending && (
              <div>{i18n('bet.pending_result')}</div>
            )}

            {!isResolved && isMatchLive && renderLiveStatus()}
          </div>
        </div>
      </div>
    )
  }

  return (
    <header className="w-full min-h-0 md:min-h-10 max-h-[98px] pt-2">
      <div className="flex flex-row justify-between items-center">
        {sportTeamLogoCard('home', sportsData.home_team_short_code, sportsData.home_team)}

        <div className="flex shrink-0 flex-col items-center gap-1 px-2">
          {league && (
            <div
              className="max-w-[7.5rem] truncate text-center text-[11px] font-medium leading-tight text-Neutral-4"
              title={league}>
              {league}
            </div>
          )}
          {headerCenterTopContent()}
          <div>{headerCenterBottomContent()}</div>
        </div>

        {sportTeamLogoCard('away', sportsData.away_team_short_code, sportsData.away_team)}
      </div>
    </header>
  )
}

export default SportEventQuestionHeader
